import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ScanFace, Loader2, X, PersonStanding, Camera, Crown, Lock, Infinity, Zap, Eye } from "lucide-react";
import ScanProgress from "@/components/app/ScanProgress";
import ScannerOverlay from "@/components/app/ScannerOverlay";
import WebcamCapture from "@/components/app/WebcamCapture";
import EliteScanPreview from "@/components/app/EliteScanPreview";
import { useAuth } from "@/lib/auth";

const MODES = [
  {
    id: "face",
    icon: ScanFace,
    title: "FACE SCAN",
    subtitle: "Quick diagnostic",
    body: "AI rates jawline, skin, eyes, harmony, symmetry, hairline. Best for selfies.",
    bullets: ["6 sub-scores", "Improvement stack", "Verdict + tier"],
  },
  {
    id: "full",
    icon: PersonStanding,
    title: "FULL SCAN",
    subtitle: "Total looksmaxx audit",
    body: "Everything in Face Scan PLUS frame, body composition, posture, sex appeal, style, halo + aura.",
    bullets: ["12 sub-scores", "Body fat % + frame class", "Masculinity index"],
  },
  {
    id: "elite",
    icon: Crown,
    title: "ELITE SCAN",
    subtitle: "VIP-only · cinematic tier",
    body: "24 sub-metrics, celebrity mog-match, canthal-tilt & gonial-angle in degrees, ideal BF% and weight targets, 10 ranked actions.",
    bullets: ["24 sub-scores", "Celeb mog-match + archetype", "10 actions w/ timeframes"],
    vipOnly: true,
  },
];

export default function Scan() {
  const nav = useNavigate();
  const { user } = useAuth();
  const fileInput = useRef(null);
  const [mode, setMode] = useState("face");
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [quota, setQuota] = useState(null);
  const [elitePreview, setElitePreview] = useState(false);
  const [topupBusy, setTopupBusy] = useState(false);

  const isVip = !!user?.is_vip;

  const loadQuota = () => api.get("/scan/quota").then((r) => setQuota(r.data)).catch(() => {});
  useEffect(() => { loadQuota(); }, []);

  const bonus = quota?.bonus_credits || 0;
  const outOfScans = quota && !quota.unlimited && quota.remaining <= 0 && bonus <= 0;

  const buyTopup = async () => {
    setTopupBusy(true);
    try {
      const { data } = await api.post("/payments/scan-topup/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setTopupBusy(false);
    }
  };

  // Human-friendly reset countdown
  const resetInText = (() => {
    if (!quota?.reset_at || quota.unlimited) return null;
    try {
      const ms = new Date(quota.reset_at).getTime() - Date.now();
      if (ms <= 0) return "any second";
      const h = Math.floor(ms / 3_600_000);
      const m = Math.floor((ms % 3_600_000) / 60_000);
      if (h > 0) return `${h}h ${m}m`;
      return `${m}m`;
    } catch { return null; }
  })();

  const handleFile = (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Use JPEG, PNG, or WEBP only");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      setBase64(dataUrl.split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (!base64) {
      toast.error("Upload or capture an image first");
      return;
    }
    if (outOfScans) {
      nav("/vip");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/scan", { image_base64: base64, mode });
      toast.success(`Scan complete. Mog score: ${data.overall_score}`);
      nav(`/scan/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402 && detail?.code === "SCAN_LIMIT_REACHED") {
        toast.error(detail.message);
        loadQuota();
        setLoading(false);
        return;
      }
      toast.error(typeof detail === "string" ? detail : "Scan failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setBase64(null);
  };

  const onWebcamCapture = ({ dataUrl, base64 }) => {
    setPreview(dataUrl);
    setBase64(base64);
    setWebcamOpen(false);
    toast.success("Snapshot locked in.");
  };

  const checklist = mode === "full"
    ? ["Full-body or upper-body shot", "Standing, natural posture", "Even lighting, neutral background", "Form-fitting clothing visible", "Clear, in-focus image", "Single subject in frame"]
    : ["Front-facing portrait", "Even, natural lighting", "Neutral expression", "No hats or sunglasses", "Clear, in-focus image", "Just one face in frame"];

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10 relative z-10">
      <div className="mb-8">
        <span className="tape-label">DIAGNOSTIC</span>
        <h1 className="font-display text-5xl md:text-7xl mt-3 leading-none">
          PICK YOUR<br /><span className="text-[var(--primary)] glow">PROTOCOL.</span>
        </h1>
        <p className="text-[var(--text-muted)] mt-4 max-w-lg">
          Choose Face or Full. Drop a file or capture live. Receive your PSL-calibrated breakdown.
        </p>
      </div>

      {/* Daily scan quota — pushes free users toward VIP */}
      {quota && (
        <div
          data-testid="scan-quota-banner"
          className={`panel-bright p-4 mb-8 flex items-center justify-between gap-4 flex-wrap border-l-4 ${
            quota.unlimited
              ? "border-[var(--primary)]"
              : outOfScans
              ? "border-[var(--danger)]"
              : "border-[var(--warning)]"
          }`}
        >
          <div className="flex items-center gap-3">
            {quota.unlimited ? (
              <Crown className="w-6 h-6 text-[var(--primary)] glow shrink-0" />
            ) : outOfScans ? (
              <Lock className="w-6 h-6 text-[var(--danger)] shrink-0" />
            ) : (
              <Zap className="w-6 h-6 text-[var(--warning)] shrink-0" />
            )}
            <div>
              <div className="font-display text-2xl leading-none">
                {quota.unlimited ? (
                  <span className="flex items-center gap-2">
                    UNLIMITED SCANS <Infinity className="w-5 h-5 text-[var(--primary)]" />
                  </span>
                ) : outOfScans ? (
                  <>DAILY SCAN USED</>
                ) : (
                  <>
                    {quota.remaining} FREE SCAN{quota.remaining === 1 ? "" : "S"} LEFT
                    {bonus > 0 && <> · <span className="text-[var(--primary)]">+{bonus} BONUS</span></>}
                  </>
                )}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                {quota.unlimited
                  ? "VIP tier active — scan as often as you want."
                  : outOfScans
                  ? <>Next free scan in <span className="text-[var(--primary)]">{resetInText || "24h"}</span> — or unlock now below.</>
                  : <>Free tier: {quota.limit} scan / 24h rolling. Next slot in {resetInText || "24h"}. VIP unlocks unlimited + Elite.</>}
              </div>
            </div>
          </div>
          {!quota.unlimited && (
            <div className="flex items-center gap-2 flex-wrap">
              {outOfScans && (
                <button
                  onClick={buyTopup}
                  disabled={topupBusy}
                  className="btn-ghost border border-[var(--primary)] text-[var(--primary)]"
                  data-testid="scan-topup-cta"
                >
                  {topupBusy ? (
                    <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</span>
                  ) : (
                    <>+1 Scan · £1.99</>
                  )}
                </button>
              )}
              <Link
                to="/vip"
                className={outOfScans ? "btn-brutal" : "btn-ghost"}
                data-testid="scan-quota-upgrade-cta"
              >
                <Crown className="inline w-4 h-4 mr-2" />
                {outOfScans ? "Unlimited · From £9.99 →" : "Go VIP · From £9.99 →"}
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Mode selector */}
      <div className="grid grid-cols-12 gap-px bg-[var(--border)] mb-8">
        {MODES.map((m) => {
          const Icon = m.icon;
          const selected = mode === m.id;
          const locked = m.vipOnly && !isVip;
          return (
            <button
              key={m.id}
              onClick={() => {
                if (locked) {
                  setElitePreview(true);
                  return;
                }
                setMode(m.id);
              }}
              data-testid={`mode-${m.id}`}
              className={`col-span-12 md:col-span-4 text-left p-6 transition-all border-l-4 relative ${
                selected
                  ? "bg-[var(--surface-2)] border-[var(--primary)]"
                  : locked
                  ? "bg-[var(--bg)] border-transparent hover:bg-[var(--surface)] opacity-80"
                  : "bg-[var(--bg)] border-transparent hover:bg-[var(--surface)]"
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <Icon className={`w-7 h-7 ${selected ? "text-[var(--primary)] glow" : m.vipOnly ? "text-[var(--primary)]" : "text-white"}`} />
                {selected && <span className="tape-label">SELECTED</span>}
                {locked && (
                  <span className="tape-label flex items-center gap-1" data-testid={`mode-${m.id}-lock`}>
                    <Lock className="w-2.5 h-2.5" /> VIP
                  </span>
                )}
                {m.vipOnly && isVip && !selected && (
                  <span className="tape-label">UNLOCKED</span>
                )}
              </div>
              <div className="font-display text-3xl tracking-wide">{m.title}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                {m.subtitle}
              </div>
              <p className="text-sm text-[var(--text-muted)] mt-4">{m.body}</p>
              <ul className="mt-4 space-y-1 text-xs font-mono">
                {m.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <span className="text-[var(--primary)]">+</span>
                    <span className="text-white">{b}</span>
                  </li>
                ))}
              </ul>
              {locked && (
                <div className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[var(--primary)] flex items-center gap-1" data-testid={`mode-${m.id}-preview-hint`}>
                  <Eye className="w-3 h-3" /> Tap to preview →
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7">
          {!preview ? (
            <div className="grid grid-cols-12 gap-3">
              {/* Dropzone */}
              <div
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                className="col-span-12 md:col-span-8 border border-dashed border-[var(--border-bright)] aspect-[4/3] flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors"
                onClick={() => fileInput.current?.click()}
                data-testid="scan-dropzone"
              >
                <Upload className="w-12 h-12 text-[var(--primary)] mb-4" />
                <div className="font-display text-3xl mb-2">
                  {mode === "full" ? "DROP FULL BODY" : "DROP YOUR FACE"}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                  Click or drag &amp; drop
                </div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files[0])}
                  data-testid="scan-file-input"
                />
              </div>
              {/* Webcam panel */}
              <button
                onClick={() => setWebcamOpen(true)}
                data-testid="open-webcam-button"
                className="col-span-12 md:col-span-4 border border-[var(--border-bright)] aspect-[4/3] md:aspect-auto flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] hover:bg-[var(--surface)] transition-all relative overflow-hidden group"
              >
                <div className="absolute inset-0 matrix-grid opacity-30 group-hover:opacity-60 transition-opacity" />
                <Camera className="w-10 h-10 text-[var(--primary)] mb-3 relative z-10 glow" />
                <div className="font-display text-2xl relative z-10">LIVE CAPTURE</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-2 relative z-10">
                  Use your webcam
                </div>
              </button>
            </div>
          ) : (
            <div className="relative panel-bright aspect-[4/3] overflow-hidden brackets">
              <span className="b-tr" />
              <span className="b-bl" />
              <img src={preview} alt="preview" className="w-full h-full object-cover" data-testid="scan-preview-image" />
              <ScannerOverlay active={loading} mode={mode} />
              {!loading && (
                <button
                  onClick={reset}
                  className="absolute top-4 right-4 bg-black/70 border border-white/20 p-2 hover:bg-[var(--primary)] hover:text-black transition-colors z-20"
                  data-testid="scan-clear-button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          <button
            onClick={submit}
            disabled={!base64 || loading}
            className={outOfScans ? "btn-brutal w-full mt-6" : "btn-brutal w-full mt-6"}
            data-testid="scan-analyze-button"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Analyzing {mode === "elite" ? "ELITE dossier" : mode === "full" ? "full body" : "face"}...
              </span>
            ) : outOfScans ? (
              <span className="flex items-center justify-center gap-2">
                <Crown className="w-4 h-4" /> Ascend for Unlimited Scans →
              </span>
            ) : (
              <>RUN {mode === "elite" ? "ELITE" : mode === "full" ? "FULL" : "FACE"} DIAGNOSTIC →</>
            )}
          </button>

          {loading && <ScanProgress mode={mode} />}
        </div>

        <aside className="col-span-12 lg:col-span-5 panel p-6">
          <span className="tape-label">CHECKLIST</span>
          <ul className="mt-6 space-y-4 text-sm font-mono">
            {checklist.map((t, i) => (
              <li key={t} className="flex items-start gap-3">
                <span className="text-[var(--primary)] w-6">0{i + 1}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 border-t border-[var(--border)] pt-6 text-xs text-[var(--text-muted)]">
            <ScanFace className="w-5 h-5 text-[var(--primary)] mb-2" />
            Scans are stored privately. Battle participation is optional.
          </div>
        </aside>
      </div>

      {webcamOpen && (
        <WebcamCapture onCapture={onWebcamCapture} onClose={() => setWebcamOpen(false)} />
      )}
      {elitePreview && <EliteScanPreview onClose={() => setElitePreview(false)} />}
    </div>
  );
}
